// src/components/eventos/views/EventoAoVivo.js
// Rota permanente por cliente: /ao-vivo/:clienteId
// Sempre exibe o evento em andamento daquele cliente.
// O link nunca muda — o cliente compartilha uma vez só.

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import EventoPublicoConteudo from './EventoPublicoConteudo';

const CalendarOffIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        <line x1="2" y1="2" x2="22" y2="22"/>
    </svg>
);

// Detecta se o parâmetro é um UUID (ex: 550e8400-e29b-41d4-a716-446655440000)
const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

const EventoAoVivo = ({ supabase }) => {
    const { clienteId: clienteParam } = useParams();
    const [evento, setEvento] = useState(null);
    const [cliente, setCliente] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        const fetchEventoAtivo = async () => {
            try {
                // Buscar cliente por slug ou por ID (compatibilidade)
                let clienteData = null;
                if (isUUID(clienteParam)) {
                    const { data } = await supabase
                        .from('clientes').select('id, nome, logo_url').eq('id', clienteParam).maybeSingle();
                    clienteData = data;
                } else {
                    const { data } = await supabase
                        .from('clientes').select('id, nome, logo_url').eq('slug', clienteParam).maybeSingle();
                    clienteData = data;
                }
                if (clienteData) setCliente(clienteData);

                const resolvedId = clienteData?.id;
                if (!resolvedId) { setEvento(null); return; }

                // 1. Buscar evento em andamento
                const { data: aoVivo, error: errAoVivo } = await supabase
                    .from('eventos').select('*')
                    .eq('cliente_id', resolvedId)
                    .eq('status', 'em_andamento')
                    .order('data', { ascending: true })
                    .limit(1)
                    .maybeSingle();
                if (errAoVivo) throw errAoVivo;

                if (aoVivo) { setEvento(aoVivo); return; }

                // 2. Sem evento ao vivo — buscar o próximo agendado
                const _d = new Date(); const hoje = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
                const { data: proximo, error: errProximo } = await supabase
                    .from('eventos').select('*')
                    .eq('cliente_id', resolvedId)
                    .eq('status', 'agendado')
                    .gte('data', hoje)
                    .order('data', { ascending: true })
                    .limit(1)
                    .maybeSingle();
                if (errProximo) throw errProximo;

                setEvento(proximo || null);
            } catch (e) {
                setErro(e.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEventoAtivo();
        const interval = setInterval(fetchEventoAtivo, 20000);
        return () => clearInterval(interval);
    }, [clienteParam, supabase]);

    if (isLoading) {
        return (
            <>
                <style>{`
                    @keyframes av-spin { to { transform: rotate(360deg); } }
                    @keyframes av-aurora1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(40px,-30px) scale(1.06);} }
                    @keyframes av-aurora2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-30px,40px) scale(1.04);} }
                `}</style>
                <div style={{ minHeight: '100vh', background: '#0A1F3A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif", position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        <div style={{ position: 'absolute', top: '-20%', left: '-15%', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(44,203,144,0.18) 0%, transparent 65%)', filter: 'blur(80px)', animation: 'av-aurora1 12s ease-in-out infinite' }} />
                        <div style={{ position: 'absolute', bottom: '-20%', right: '-15%', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(45,184,225,0.14) 0%, transparent 65%)', filter: 'blur(80px)', animation: 'av-aurora2 16s ease-in-out infinite' }} />
                    </div>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid rgba(44,203,144,0.2)', borderTopColor: '#2CCB90', animation: 'av-spin 0.8s linear infinite', position: 'relative', zIndex: 1 }} />
                </div>
            </>
        );
    }

    if (erro || !clienteParam) {
        return (
            <div style={{ minHeight: '100vh', background: '#0A1F3A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                    <p style={{ color: '#f87171', fontSize: '16px', fontWeight: 600 }}>Link inválido</p>
                </div>
            </div>
        );
    }

    // Nenhum evento ao vivo no momento
    if (!evento) {
        return (
            <>
                <style>{`
                    @keyframes av-pulse  { 0%,100%{opacity:1} 50%{opacity:0.4} }
                    @keyframes av-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
                    @keyframes av-aurora1 { 0%,100%{transform:translate(0,0) scale(1); opacity:0.8;} 50%{transform:translate(60px,-40px) scale(1.08); opacity:1;} }
                    @keyframes av-aurora2 { 0%,100%{transform:translate(0,0) scale(1); opacity:0.7;} 50%{transform:translate(-50px,60px) scale(1.05); opacity:0.9;} }
                `}</style>
                <div style={{ minHeight: '100vh', background: '#0A1F3A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', -apple-system, sans-serif", padding: '32px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>

                    {/* Aurora */}
                    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
                        <div style={{ position: 'absolute', top: '-20%', left: '-15%', width: '65vw', height: '65vw', maxWidth: '650px', maxHeight: '650px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(44,203,144,0.2) 0%, transparent 70%)', filter: 'blur(80px)', animation: 'av-aurora1 14s ease-in-out infinite' }} />
                        <div style={{ position: 'absolute', bottom: '-20%', right: '-15%', width: '60vw', height: '60vw', maxWidth: '600px', maxHeight: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(45,184,225,0.16) 0%, transparent 70%)', filter: 'blur(80px)', animation: 'av-aurora2 18s ease-in-out infinite' }} />
                    </div>

                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                        {/* Logo */}
                        <div style={{ marginBottom: '40px' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', background: '#fff', borderRadius: '10px', padding: '6px 16px', border: '1px solid rgba(255,255,255,0.15)' }}>
                                <img src="/logo-horizontal.png" alt="Evlo" style={{ height: '24px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                            </div>
                        </div>

                        {/* Badge do cliente */}
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 18px', borderRadius: '100px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', marginBottom: '36px' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', animation: 'av-pulse 2s ease-in-out infinite', flexShrink: 0 }} />
                            <span style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                {cliente?.nome || 'Evlo'}
                            </span>
                        </div>

                        {/* Ícone */}
                        <div style={{ color: 'rgba(255,255,255,0.2)', marginBottom: '28px', animation: 'av-float 3s ease-in-out infinite' }}>
                            <CalendarOffIcon />
                        </div>

                        <h1 style={{ margin: '0 0 12px', fontSize: 'clamp(24px,4vw,36px)', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.03em' }}>
                            Nenhum evento ao vivo agora
                        </h1>
                        <p style={{ margin: '0 0 36px', fontSize: '16px', color: 'rgba(255,255,255,0.45)', maxWidth: '420px', lineHeight: 1.6 }}>
                            Quando um evento iniciar, a programação completa aparecerá aqui automaticamente.
                        </p>

                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '100px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'rgba(255,255,255,0.25)', animation: 'av-pulse 1.5s ease-in-out infinite' }} />
                            Atualizando automaticamente
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Evento ao vivo — reutiliza o conteúdo do EventoPublico
    return <EventoPublicoConteudo evento={evento} cliente={cliente} />;
};

export default EventoAoVivo;
